/* Avatar.
 *
 * Falls back to initials on a deterministic colour when there is no image —
 * which is most people, most of the time. The colour comes from a hash of the
 * name (lib/utils), so someone looks the same everywhere in the product.
 */
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn, avatarColor, initials } from '@/lib/utils'

const SIZES = {
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-[52px] w-[52px] text-lg',
  xl: 'h-20 w-20 text-2xl',
} as const

export interface AvatarProps {
  name: string
  src?: string | null
  size?: keyof typeof SIZES
  className?: string
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        'relative flex shrink-0 overflow-hidden rounded-pill select-none',
        SIZES[size],
        className,
      )}
    >
      {src ? (
        <AvatarPrimitive.Image
          src={src}
          alt={name}
          className="h-full w-full object-cover"
        />
      ) : null}
      <AvatarPrimitive.Fallback
        // No delay: a delay shows an empty circle first, which reads as a
        // broken image on a feed that is already loading.
        delayMs={0}
        className="flex h-full w-full items-center justify-center font-bold tracking-tight text-white"
        // A flat disc of saturated colour is the loudest thing on the page. A
        // shallow top-left-to-bottom-right ramp reads as a surface instead.
        // colour-mix keeps this one line rather than a second palette.
        style={{
          backgroundImage: `linear-gradient(135deg, ` +
            `color-mix(in srgb, ${avatarColor(name)} 82%, white) 0%, ` +
            `color-mix(in srgb, ${avatarColor(name)} 92%, black) 100%)`,
        }}
      >
        {initials(name)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}
